
// --- Vue & Template imports ---
import { Component, Vue} from 'vue-property-decorator';

// --- Models ---
import { Driver } from '@/models/Driver.model';
import { DynamicCardState } from '@/models/App.model';
import { Load, LoadEvent, LoadStop } from '@/models/Load.model';

// --- Services ---
import AppService from '@/services/app';
import DriverService from '@/services/driver';
import LoadEventService from '@/services/loadEvent';
import LoadExtraService from '@/services/loadExtra';
import LoadService from '@/services/load';
import VehicleService from '@/services/vehicle';

// --- Third Party ---
import dayjs from 'dayjs';
import Hashids from 'hashids';

@Component
export default class ViewLoadPage extends Vue {
  hashids = new Hashids(process.env.VUE_APP_HASHIDS_SALT);

  clickedItem: Load = Object.assign({}, this.$store.getters['loads/emptyLoad']);

  rateString: string = '';
  finalPriceString: string = '';

  loadStops: LoadStop[] = [];
  loadEvents: LoadEvent[] = [];

  // #region Functions to display load data on page load
    offloadHeaders() {
      return [
        {
          text: 'Client',
          value: 'client',
        },
        {
          text: 'Address',
          value: 'address',
        },
        {
          text: 'Appointment Time',
          value: 'appointmentTime',
        },
        {
          text: 'Arrived',
          value: 'arrivedAt',
        },
      ];
    }

    async beforeMount() {
      const id: string = this.hashids.decode(this.$route.params.id) + '';

      if (id && id.length > 0) {
        this.clickedItem.id = parseInt(id, 10);

        let fetchLoadFromDB: boolean = true;
        let foundIndex = -1;

        await AppService.listStatuses(false);
        await VehicleService.listTypes(false);
        await VehicleService.listActive(true);
        await DriverService.listActive(true);
        await LoadExtraService.listExtraTypes(false);

        if (this.$store.state.from === 'Movement') {
          // try to find this load in the the movement loads because we clicked thru from there (so we know they're already in our store)
          foundIndex = this.$store.getters['loads/movementLoads'].findIndex( (load: Load) =>  load.id === this.clickedItem.id);

          if (foundIndex >= 0) {
            // we found it
            this.clickedItem = this.$store.getters['loads/movementLoads'][foundIndex];
            fetchLoadFromDB = false;
          }
        } else if (this.$store.state.from === 'Approvals') {
          // try to find this load in the the approval loads because we clicked thru from there (so we know they're already in our store)
          foundIndex = this.$store.getters['loads/approvalLoads'].findIndex( (load: Load) =>  load.id === this.clickedItem.id);

          if (foundIndex >= 0) {
            // we found it
            this.clickedItem = this.$store.getters['loads/approvalLoads'][foundIndex];
            fetchLoadFromDB = false;            
          }
        }

        if (fetchLoadFromDB) {
          // this didn't come from Movement or Approvals View link, or something else weird happened because we couldnt find it in the store
          await this.getLoad();
        } else {
          this.rateString = this.clickedItem.rate.toFixed(2);
          this.finalPriceString = this.clickedItem.finalPrice.toFixed(2);
        }

        await this.listOffloads();
        await this.listLoadEvents();
        
      } else {
        // kick  them out, how did they even get here?!
        this.$router.push({ path: '/'});
      }
    }

    goBack() {
      this.$router.push({ name: this.$store.state.from });
    }

    async getLoad() {
      try {
        const response = await LoadService.getLoad({ params: { loadId: this.clickedItem.id }});

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            AppService.errorHandler(response.data.message);
            // kick  them out, we have no load for this Id
            this.$router.push({ path: '/'});
          } else {
            this.clickedItem = response.data;
            this.rateString = this.clickedItem.rate.toFixed(2);
            this.finalPriceString = this.clickedItem.finalPrice.toFixed(2);
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {

        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Load_View.vue - getLoad - 798 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }
        
        // kick  them out, we have no load for this Id
        this.$router.push({ path: '/'});
      }
    }

    showDriverWarning(type: string, driverId: number) {
      const selectedDriver = this.$store.getters['drivers/activeDrivers'].find((item: Driver) => item.id === driverId);
      if (selectedDriver) {
        if (!selectedDriver.deletedAt) {
          switch (type) {
            case 'pdp':
              if (!selectedDriver.hasReceipt) {
                const pdpExpiryDate = dayjs(selectedDriver.pdpExpiry);

                if (dayjs().isAfter(pdpExpiryDate)) {
                  return 'overdue';
                } else if (dayjs().isAfter(pdpExpiryDate.subtract(21, 'days'))) {
                  return 'pdp-due';
                }
              } else {
                return 'has-pdp-receipt';
              }
              break;
          }
        }
      }
      return '';
    }

    async listOffloads() {
      try {
        const response = await LoadService.listOffloadsByLoad({ params: { loadId: this.clickedItem.id }});

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            this.loadStops = [];
          } else {
            this.loadStops = response.data;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {

        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Load_View.vue - listOffloads - 851 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }
      }
    }

    async listLoadEvents() {
      try {
        // always check for all the comments in case someone else added one in the background - until we are using websockets
        const response = await LoadEventService.listByLoad({params: {loadId: this.clickedItem.id}});

        if (response && response.data) {
          if (response.data.result && response.data.result === 'false') {
            this.loadEvents = [];
          } else {
           this.loadEvents = response.data;
          }
        } else {
          // response is undefined or has no data field - SHOULD NEVER HAPPEN!
          throw new Error('response: ' + JSON.stringify(response));
        }

      } catch (error) {
        if (error.response) {
          AppService.errorHandler(error.response.statusText);
        } else {
          AppService.logSupportDebug('Load_View.vue - listLoadEvents - 877 - ' + error);
          AppService.errorHandler(this.$store.getters['app/messages'].couldNotConnect);
        }
      }
    }

    getLoadCardState() {
      let result: DynamicCardState = {
        headerIcon: this.$store.getters['app/icons'].vehicles,
        headerBackgroundColor: 'primary',      
      };

      // Statuses in use:
      // WAIT_PROCESSING / WAIT_INVOICE_NO / WAIT_INVOICE_SEND should be grey with spinners/loaders
      // Offloaded 
      // New Load
      // Loading
      // En Route
      // Arrived
      
      // WAIT_OPPROVE
      // WAIT_POD_SCAN
      // SCANNED
      // REJECTED should be red


      switch (this.clickedItem.statusId) {
        case 0:
          break;
          
        case this.$store.getters['app/statusHelper'].rejected:
          // User sees red icon when Load has status 11:REJECTED
          result = {
            headerIcon: this.$store.getters['app/statusById'](this.clickedItem.statusId).icon,
            headerBackgroundColor: 'red',
            tooltip: this.clickedItem.statusName,
          };
          break; 

        case this.$store.getters['app/statusHelper'].waitInvoiceNo: 
        case this.$store.getters['app/statusHelper'].waitProcessing:
        case this.$store.getters['app/statusHelper'].waitInvoiceSend:
          // User sees Loader when Load has status 25 - WAIT_INVOICE_NO / 30 - WAIT_PROCESSING / 35 - WAIT_INVOICE_SEND : Load procedure in process
          result = {
            headerBackgroundColor: 'grey',
            tooltip: this.clickedItem.statusName,
            loader: true,
          };
          break;

        default:
          result = {
            headerIcon: this.$store.getters['app/statusById'](this.clickedItem.statusId).icon,
            headerBackgroundColor: 'primary',
            tooltip: this.clickedItem.statusName,
          };
          break;
      }

      return result;
    }
  // #endregion
}
